import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ClearIcon from '@mui/icons-material/CloseOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNewOutlined';
import { useFilteredRows } from '../hooks/useFilteredRows';
import { useData } from '../store/data';
import { useFilters } from '../store/filters';
import { countBy, sortedByCount } from '../lib/filter-rows';
import { isDone } from '../lib/status';
import { fmtInt } from '../lib/format';
import { StatusChip } from './StatusChip';
import type { Issue } from '../types/issue';

const JIRA_URL_KEY = 'v2.jira.baseUrl';
function loadJiraBaseUrl(): string {
  if (typeof window === 'undefined') return 'https://schueco.atlassian.net';
  return (localStorage.getItem(JIRA_URL_KEY) || 'https://schueco.atlassian.net').replace(/\/+$/, '');
}
const jiraUrl = (base: string, key: string) => `${base}/browse/${encodeURIComponent(key)}`;

// Alle durchsuchbaren Textfelder (Reihenfolge = UI-Reihenfolge)
const SEARCH_FIELDS = [
  { key: 'Summary',                             label: 'Summary (Titel)',        defaultOn: true  },
  { key: 'Custom field (Expected Behaviour)',   label: 'Expected Behaviour',     defaultOn: true  },
  { key: 'Parent summary',                      label: 'Parent Summary',         defaultOn: false },
  { key: 'Labels',                              label: 'Labels',                 defaultOn: false },
  { key: 'Assignee',                            label: 'Assignee',               defaultOn: false },
  { key: 'Issue key',                           label: 'Issue Key',              defaultOn: false },
] as const;

type FieldKey = typeof SEARCH_FIELDS[number]['key'];
type StatusScope = 'open' | 'closed' | 'all';

type Match = {
  row:    Issue;
  hits:   { field: FieldKey; text: string; index: number }[];
  score:  number;
};

export function TicketSearch() {
  const rowsFull = useFilteredRows() as Issue[];
  const allRows  = useData(s => s.rows) as Issue[];
  const teamFilter = useFilters(s => s.team);
  const setFilters = useFilters(s => s.set);
  const jiraBase = useMemo(() => loadJiraBaseUrl(), []);

  const [rawQuery, setRawQuery]     = useState('');
  const [query, setQuery]           = useState('');    // debounced
  const [useRegex, setUseRegex]     = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [enabledFields, setEnabledFields] = useState<Set<FieldKey>>(
    () => new Set(SEARCH_FIELDS.filter(f => f.defaultOn).map(f => f.key as FieldKey))
  );
  const [scope, setScope] = useState<StatusScope>('open');
  const [regexError, setRegexError] = useState<string | null>(null);

  // Debounce: 250 ms nach dem letzten Tastendruck suchen
  useEffect(() => {
    const t = setTimeout(() => setQuery(rawQuery), 250);
    return () => clearTimeout(t);
  }, [rawQuery]);

  const allTeams = useMemo(
    () => sortedByCount(countBy(allRows, 'Team Name')),
    [allRows]
  );

  const scopedRows = useMemo(() => {
    if (scope === 'all') return rowsFull;
    return rowsFull.filter(r => {
      const done = isDone(String(r['Status'] ?? ''));
      return scope === 'open' ? !done : done;
    });
  }, [rowsFull, scope]);

  // Regex kompilieren (einmal pro Query-Change)
  const matcher = useMemo(() => {
    setRegexError(null);
    const q = query.trim();
    if (!q) return null;
    if (useRegex) {
      try {
        return new RegExp(q, caseSensitive ? 'g' : 'gi');
      } catch (e) {
        setRegexError((e as Error).message);
        return null;
      }
    }
    // Plain substring — escapen und in Regex packen, damit wir Highlight-
    // Indizes einheitlich behandeln.
    const esc = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(esc, caseSensitive ? 'g' : 'gi');
  }, [query, useRegex, caseSensitive]);

  const results = useMemo<Match[]>(() => {
    if (!matcher) return [];
    const out: Match[] = [];
    const MAX = 500;
    for (const r of scopedRows) {
      const hits: Match['hits'] = [];
      let score = 0;
      for (const field of enabledFields) {
        const v = (r as Record<string, unknown>)[field];
        if (v == null || v === '') continue;
        const s = Array.isArray(v) ? v.join(' ') : String(v);
        matcher.lastIndex = 0;
        const m = matcher.exec(s);
        if (m) {
          hits.push({ field, text: s, index: m.index });
          // Summary-Treffer wiegt mehr als andere Felder
          score += field === 'Summary' ? 10 : field === 'Issue key' ? 20 : 5;
        }
      }
      if (hits.length) {
        out.push({ row: r, hits, score });
        if (out.length >= MAX) break;
      }
    }
    out.sort((a, b) => b.score - a.score);
    return out;
  }, [scopedRows, enabledFields, matcher]);

  const clearQuery = useCallback(() => setRawQuery(''), []);

  function toggleField(k: FieldKey) {
    const next = new Set(enabledFields);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setEnabledFields(next);
  }

  return (
    <Stack spacing={2}>
      {/* Team-Chip-Reihe (wirkt global) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', p: 1, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, mr: 0.5, color: 'text.secondary' }}>
          TEAM
        </Typography>
        <Chip
          size="small"
          label="Alle"
          color={teamFilter === '' ? 'primary' : 'default'}
          variant={teamFilter === '' ? 'filled' : 'outlined'}
          onClick={() => setFilters({ team: '' })}
          clickable
        />
        {allTeams.map(([name, count]) => (
          <Chip
            key={name}
            size="small"
            label={`${name} (${count})`}
            color={teamFilter === name ? 'primary' : 'default'}
            variant={teamFilter === name ? 'filled' : 'outlined'}
            onClick={() => setFilters({ team: name })}
            clickable
          />
        ))}
      </Box>

      {/* Suchfeld + Optionen */}
      <Card variant="outlined">
        <CardContent sx={{ py: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder='Suchbegriff eingeben (z.B. "Falzhöhe", "K10986", "Griff klemmt") — Enter/Debounce startet die Suche automatisch'
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            error={!!regexError}
            helperText={regexError ?? undefined}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
                endAdornment: rawQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearQuery} edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', mr: 1 }}>FELDER</Typography>
              {SEARCH_FIELDS.map(f => (
                <FormControlLabel
                  key={f.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={enabledFields.has(f.key as FieldKey)}
                      onChange={() => toggleField(f.key as FieldKey)}
                    />
                  }
                  label={<Typography variant="body2">{f.label}</Typography>}
                  sx={{ mr: 1 }}
                />
              ))}
            </Box>
            <Box sx={{ flex: 1 }} />
            <ToggleButtonGroup
              size="small"
              value={scope}
              exclusive
              onChange={(_, v) => v && setScope(v as StatusScope)}
            >
              <ToggleButton value="open">nur offen</ToggleButton>
              <ToggleButton value="closed">nur geschlossen</ToggleButton>
              <ToggleButton value="all">alle</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={<Checkbox size="small" checked={caseSensitive} onChange={(e) => setCaseSensitive(e.target.checked)} />}
              label={<Typography variant="body2">Aa</Typography>}
              title="Case-sensitive"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={useRegex} onChange={(e) => setUseRegex(e.target.checked)} />}
              label={<Typography variant="body2">.*</Typography>}
              title="Regex"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Status + Ergebnis-Header */}
      {query && !regexError && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            size="small"
            color={results.length > 0 ? 'primary' : 'default'}
            label={
              results.length === 0
                ? `Keine Treffer für „${query}"`
                : `${fmtInt(results.length)} Treffer${results.length >= 500 ? ' (max. 500 angezeigt)' : ''}`
            }
          />
          <Typography variant="caption" color="text.secondary">
            durchsucht: {enabledFields.size} Felder · {fmtInt(scopedRows.length)} Tickets
            {teamFilter && <> · Team „{teamFilter}"</>}
          </Typography>
        </Stack>
      )}

      {/* Info wenn keine Suchbegriff */}
      {!query && (
        <Alert severity="info" icon={<SearchIcon />}>
          Gib einen Suchbegriff ein — die Suche startet automatisch nach 250 ms. Kombinierbar mit
          Team-Chips oben und Status-Toggle. Tipp: mit aktiviertem Regex kannst du z. B.
          <code>&nbsp;K\d+_\d+&nbsp;</code> nutzen um alle K-Nummern zu finden.
        </Alert>
      )}

      {/* Ergebnisse */}
      {query && !regexError && results.length > 0 && (
        <Card variant="outlined">
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Treffer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map(({ row, hits }) => {
                    const key = String(row['Issue key'] ?? '');
                    return (
                      <TableRow key={key} hover sx={{ verticalAlign: 'top' }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          <Link href={jiraUrl(jiraBase, key)} target="_blank" rel="noopener noreferrer" underline="hover"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {key}
                            <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.6 }} />
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={String(row['Issue Type'] ?? '')} variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>{String(row['Team Name'] ?? '–')}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}><StatusChip status={String(row['Status'] ?? '')} /></TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          <Stack spacing={0.5}>
                            {hits.slice(0, 3).map(h => (
                              <HighlightSnippet
                                key={h.field}
                                field={h.field}
                                text={h.text}
                                queryPattern={matcher!}
                              />
                            ))}
                            {hits.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{hits.length - 3} weitere Feld-Treffer
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}

// ─── Highlight-Snippet ──────────────────────────────────────────────────

function HighlightSnippet({ field, text, queryPattern }: {
  field: string;
  text: string;
  queryPattern: RegExp;
}) {
  // Kontext-Fenster: ±80 Zeichen um erstes Match
  const q = new RegExp(queryPattern.source, queryPattern.flags);
  q.lastIndex = 0;
  const first = q.exec(text);
  if (!first) return <Typography variant="caption">{text.slice(0, 200)}</Typography>;
  const start = Math.max(0, first.index - 80);
  const end   = Math.min(text.length, first.index + first[0].length + 80);
  const slice = text.slice(start, end);
  const prefix = start > 0 ? '… ' : '';
  const suffix = end < text.length ? ' …' : '';

  // Innerhalb des Slices alle Matches highlighten
  const localMatcher = new RegExp(queryPattern.source, queryPattern.flags);
  const parts: (string | { hit: string })[] = [];
  let cursor = 0;
  let m: RegExpExecArray | null;
  while ((m = localMatcher.exec(slice)) !== null) {
    if (m.index > cursor) parts.push(slice.slice(cursor, m.index));
    parts.push({ hit: m[0] });
    cursor = m.index + m[0].length;
    if (m[0].length === 0) break;
  }
  if (cursor < slice.length) parts.push(slice.slice(cursor));

  return (
    <Box sx={{ lineHeight: 1.5 }}>
      <Tooltip title={text} arrow>
        <Chip
          size="small"
          label={fieldLabel(field)}
          variant="outlined"
          sx={{ height: 18, fontSize: 10, mr: 0.75, verticalAlign: 'middle' }}
        />
      </Tooltip>
      <Typography component="span" variant="caption" sx={{ color: 'text.primary' }}>
        {prefix}
        {parts.map((p, i) =>
          typeof p === 'string' ? (
            <span key={i}>{p}</span>
          ) : (
            <mark key={i} style={{ backgroundColor: '#ffe082', color: '#5d4037', padding: '0 2px', borderRadius: 2 }}>
              {p.hit}
            </mark>
          )
        )}
        {suffix}
      </Typography>
    </Box>
  );
}

function fieldLabel(k: string): string {
  if (k === 'Custom field (Expected Behaviour)') return 'EB';
  if (k === 'Parent summary') return 'Parent';
  if (k === 'Issue key')      return 'Key';
  return k;
}
